import { capitalize } from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import i18n from "src/i18n/config";
import { getReleasedImages } from "src/services/imageService";

import { columnFilter } from "@dashboard/devices/components/Table/filters/DeviceTableFilters.utils";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { TableRow, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import SemverService from "@services/semverService/semverService";
import { DateCell } from "@shared/table/DateCell";
import { AndroidVersionHeader } from "@shared/table/headers/AndroidVersionHeader";
import { ChannelHeader } from "@shared/table/headers/ChannelHeader";
import { ProductNameHeader } from "@shared/table/headers/ProductNameHeader";
import {
    MinimalTable,
    MinimalTableCell,
    MinimalTableContainer,
    MinimalTableHeader,
    TruncatedText,
} from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { showErrorToast } from "@shared/toasts/Toasts";
import {
    ColumnFiltersState,
    createColumnHelper,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";

import { ReleaseNotesRow } from "./ReleaseNotesRow";
import { ReleasedImage } from "./types";

const columnHelper = createColumnHelper<ReleasedImage>();

const rowsPerPageOptions = [5, 10, 25];

const formatAndroidVersion = (version: string) => {
    const major = SemverService.getMajorVersion(version);
    return major !== null ? i18n.t("common:label.androidWithValue", { version: major }) : "";
};

const ReleaseNotesPage = () => {
    const { t } = useTranslation(["common", "pages"]);
    const location = useLocation();

    const [releasedImages, setReleasedImages] = React.useState<ReleasedImage[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [sorting, setSorting] = React.useState<SortingState>([{ id: "timestamp", desc: true }]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([{ id: "channel", value: ["Live", "Beta"] }]);

    React.useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            try {
                setReleasedImages(await getReleasedImages());
            } catch (_error) {
                showErrorToast(t("pages:releaseNotesDevicePage.toast.fetchImagesError"));
            } finally {
                setLoading(false);
            }
        };
        void fetchData();
    }, [location.search, t]);

    const columns = React.useMemo(
        () => [
            columnHelper.display({
                id: "collapse",
                header: "",
                cell: () => null,
            }),
            columnHelper.accessor("productName", {
                id: "productName",
                header: t("common:label.product"),
                cell: (info) => (
                    <MinimalTableCell fluid fluidMinWidth="24ch">
                        <TruncatedText title={info.getValue()} />
                    </MinimalTableCell>
                ),
                enableSorting: true,
                filterFn: columnFilter,
            }),
            columnHelper.accessor("version", {
                id: "version",
                header: t("common:label.version"),
                cell: (info) => (
                    <MinimalTableCell width={120}>
                        <Typography>{info.getValue()}</Typography>
                    </MinimalTableCell>
                ),
                enableSorting: true,
                sortingFn: (rowA, rowB) => SemverService.compare(rowA.original.version, rowB.original.version),
                filterFn: (row, _columnId, filterValue: string[]) =>
                    filterValue.includes(formatAndroidVersion(row.original.version)),
            }),
            columnHelper.accessor("timestamp", {
                id: "timestamp",
                header: t("common:label.date"),
                cell: (info) => <DateCell value={info.getValue()} width={140} />,
                enableSorting: true,
                sortingFn: (rowA, rowB) =>
                    new Date(rowA.original.timestamp).getTime() - new Date(rowB.original.timestamp).getTime(),
            }),
            columnHelper.accessor((row) => capitalize(row.channel), {
                id: "channel",
                header: t("common:label.channel"),
                cell: (info) => (
                    <MinimalTableCell width={140}>
                        <Typography>{info.getValue()}</Typography>
                    </MinimalTableCell>
                ),
                enableSorting: true,
                filterFn: columnFilter,
            }),
        ],
        [t],
    );

    const table = useReactTable<ReleasedImage>({
        data: releasedImages,
        columns,
        state: { columnFilters, sorting },
        initialState: { pagination: { pageSize: rowsPerPageOptions[0], pageIndex: 0 } },
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const { pageIndex, pageSize } = table.getState().pagination;

    const onColumnFiltersChange: React.Dispatch<React.SetStateAction<ColumnFiltersState>> = (updater) => {
        setColumnFilters(updater);
        table.setPageIndex(0);
    };

    return (
        <PageContainer scrollable>
            <MinimalTableContainer>
                <MinimalTable>
                    <MinimalTableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Header key={header.id} {...header}>
                                        {header.column.id === "productName" && (
                                            <ProductNameHeader
                                                productNames={releasedImages.map((image) => image.productName)}
                                                columnFilters={columnFilters}
                                                setColumnFilters={onColumnFiltersChange}
                                            />
                                        )}
                                        {header.column.id === "version" && (
                                            <AndroidVersionHeader
                                                columnId="version"
                                                androidVersions={releasedImages.map((image) =>
                                                    formatAndroidVersion(image.version),
                                                )}
                                                columnFilters={columnFilters}
                                                setColumnFilters={onColumnFiltersChange}
                                            />
                                        )}
                                        {header.column.id === "channel" && (
                                            <ChannelHeader
                                                columnFilters={columnFilters}
                                                setColumnFilters={onColumnFiltersChange}
                                            />
                                        )}
                                    </Header>
                                ))}
                            </TableRow>
                        ))}
                    </MinimalTableHeader>
                    <TableBody<ReleasedImage>
                        table={table}
                        loading={loading}
                        noDataMessage={t("pages:releaseNotesPage.noReleasedImages")}
                        filter={{
                            isActive: !!columnFilters.length && releasedImages.length > 0,
                            onResetFilter: () => onColumnFiltersChange([]),
                        }}
                    >
                        {table.getRowModel().rows.map((row) => (
                            <ReleaseNotesRow key={row.id} row={row} />
                        ))}
                    </TableBody>
                </MinimalTable>
            </MinimalTableContainer>
            <CustomTablePagination
                component="div"
                rowsPerPageOptions={rowsPerPageOptions}
                count={table.getFilteredRowModel().rows.length}
                rowsPerPage={pageSize}
                page={pageIndex}
                onPageChange={(_event, page) => table.setPageIndex(page)}
                onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value) || rowsPerPageOptions[0])}
            />
        </PageContainer>
    );
};

export default withMetaDescription(ReleaseNotesPage, {
    title: i18n.t("pages:releaseNotesPage.metaTitle"),
    description: i18n.t("pages:releaseNotesPage.metaDescription"),
});
